import { useGetFailedQuestions } from '../../../../services/userQuestion';
import { EmptyCase } from '../../../../components';
import { Loader } from '../../../../components/Loader/Loader';
import { type IQuestion } from '../../../../types/question';
import { type ISubject } from '../../../../types/subject';
import { IconAlertCircle, IconCheck, IconBook } from '@tabler/icons-react';
import clsx from 'clsx';

interface QuestionCardProps {
  question: IQuestion
}

const QuestionCard = ({ question }: QuestionCardProps) => {
  const subject = question.subject as ISubject;
  const subjectColor = subject?.color || '#3b82f6';

  return (
    <div className={clsx(
      'relative overflow-hidden rounded-lg border transition-all duration-300 mb-3',
      'p-4',
      // Light mode
      'bg-white border-gray-200 shadow-sm hover:shadow-md',
      // Dark mode
      'dark:bg-surface-dark dark:border-borders dark:shadow-lg dark:hover:shadow-xl',
      // Hover effect
      'hover:border-opacity-80'
    )}>
      {/* Colored indicator bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: subjectColor }}
      />

      <div className="ml-3">
        {/* Header with subject */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: subjectColor }}
            />
            <span className={clsx(
              'text-xs font-semibold uppercase tracking-wide',
              'text-gray-700 dark:text-surface-light'
            )}>
              {subject?.name || 'Sin materia'}
            </span>
          </div>
          <div className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          )}>
            <IconAlertCircle size={12} />
            <span>Fallida</span>
          </div>
        </div>

        {/* Question */}
        <div className="mb-3">
          <div className="flex items-start gap-2 mb-2">
            <IconBook size={16} className={clsx(
              'mt-0.5 flex-shrink-0',
              'text-gray-400 dark:text-surface-light'
            )} />
            <h4 className={clsx(
              'font-medium text-sm leading-relaxed',
              'text-gray-900 dark:text-white'
            )}>
              {question.title}
            </h4>
          </div>
        </div>

        {/* Correct answer */}
        <div className={clsx(
          'p-3 rounded-lg border-l-4',
          'bg-green-50 dark:bg-green-900/20',
          'border-green-500 dark:border-green-400'
        )}>
          <div className="flex items-start gap-2">
            <IconCheck size={16} className={clsx(
              'mt-0.5 flex-shrink-0',
              'text-green-600 dark:text-green-400'
            )} />
            <div className="flex-1">
              <span className={clsx(
                'text-xs font-semibold uppercase tracking-wide mb-1 block',
                'text-green-700 dark:text-green-300'
              )}>
                Respuesta correcta:
              </span>
              <p className={clsx(
                'text-sm font-medium',
                'text-green-800 dark:text-green-200'
              )}>
                {question.correctAnswer}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FailedQuestions = () => {
  const { questions, loading } = useGetFailedQuestions();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader size={200} />
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="py-8">
        <EmptyCase text='No hay preguntas que mostrar' />
      </div>
    );
  }

  return (
    <div className="w-full space-y-0">
      <div className={clsx(
        'mb-4 pb-3 border-b',
        'border-gray-200 dark:border-borders'
      )}>
        <h3 className={clsx(
          'text-sm font-semibold',
          'text-gray-700 dark:text-surface-light'
        )}>
          {questions.length} {questions.length === 1 ? 'pregunta' : 'preguntas'} para reforzar
        </h3>
      </div>

      <div className="space-y-3 max-h-[calc(43vh-100px)] overflow-y-auto pr-2">
        {questions.map((item) => (
          <QuestionCard key={item._id} question={item.question} />
        ))}
      </div>
    </div>
  );
};

export { FailedQuestions };
